//Libraries
import {FC, useEffect, useState} from 'react';

//Typing
import {Draft} from 'src/types/common';
import {IDIRecord, IDI} from 'src/types/org/di/diInterface';
import {IOpsRequest} from 'src/types/org/operation/requestInterface';

//Components
import {ContainerInline, ErrorNotice, HeaderInline, Loader} from 'src/generic/component';
import {ErrorManager, ERRORS_EMPTY, ValidationError} from "./../../../../types";
import {OrgFields} from "./../orgFields";
import {useAuth} from "./../../../../contexts";
import {IOrgModel} from "./../../../../types/org/orgInterface";
import {Button, PrimaryButton} from "@cb/apricot-react";
import {DiFields} from "../di/diFields";
import { validate } from "src/types/org/di/diRecordSchema";
import {API} from "./../../../../apis/apiWrapper";
import {processResponse} from "./../../../../utils/api";
import {Dialog} from "@mui/material";
import {disableDismiss, enableDismiss} from "./../../../../generic/utils/modal";
import { isUS } from "src/types";

interface IDiProfileEditFormProps {
    record: IDIRecord;
    onComplete: () => void;
    onClose: () => void;
}

export const DiProfileEditForm: FC<IDiProfileEditFormProps> = ({record, onComplete, onClose}) => {

    const {lastName, firstName, roles} = useAuth();
    const isAdmin = roles.includes('XPM_ADMIN') || roles.includes('AI_ADMIN');
    const allowProtected = isAdmin;
    // Post okta migrations, userNames start with 'Z9Z9P',replacing it with lastName, firstName
    const displayUserName = lastName + ', ' + firstName;
    const preparedByUser = displayUserName;
    const approvedByUser = displayUserName;


    const [org, setOrg] = useState<Draft<IOrgModel>>({});
    const [di, setDi] = useState<Draft<IDI>>({});
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('');
    const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);
    const showLoading = submitting;

    let errors = ErrorManager(ERRORS_EMPTY);

    useEffect(() => {
        const data = record;
        setOrg(data?.org || {});
        setDi(data?.di || {});
    }, [record]);

    if (showLoading) {
        return <Loader/>;
    } else {
        let request: IOpsRequest<IDIRecord> | undefined = undefined;
        let validated: boolean = false;
        try {
            const {apAddress} = di;
            if(apAddress){ 
                apAddress.streetAddr2 = apAddress.streetAddr2 || undefined;
                apAddress.streetAddr3 = apAddress.streetAddr3 || undefined;
                apAddress.intlPostalCd = apAddress.intlPostalCd || undefined;
                apAddress.regionCd = apAddress.regionCd || undefined; 
                apAddress.province = apAddress.province || undefined; 
                apAddress.stateCd = apAddress.stateCd || undefined; 
                apAddress.zip5 = apAddress.zip5 || undefined;
                apAddress.zip4 = apAddress.zip4 || undefined;        
            }

            let hasAddress = false;
            if (
                apAddress?.streetAddr1 ||
                (apAddress?.streetAddr2 && apAddress?.streetAddr2 !== null ) ||
                (apAddress?.streetAddr3 && apAddress?.streetAddr3 !== null ) ||
                apAddress?.city ||
                (isUS(apAddress?.countryIsoCd || "") && ( (apAddress?.stateCd  && apAddress?.stateCd  !== null)|| (apAddress?.zip5 && apAddress?.zip5 !== null ) || (apAddress?.zip4 && apAddress?.zip4 !== null))) ||
                (!isUS(apAddress?.countryIsoCd || "") && ((apAddress?.province && apAddress?.province !== null) || (apAddress?.intlPostalCd && apAddress?.intlPostalCd !== null)))
              ) {
                hasAddress = true;
              }
            

            const data = validate(
                {
                org: org,
                di: {...di,
                    apContact: di.apEligibilityInd ? di.apContact : undefined,
                    satContact: di.satEligibilityInd ? di.satContact : undefined,
                    // if apEligibilityInd is false, we should not send apAddress key itself to API, hence undefined in this case
                    //if apEligibility is true and  can be either(1) hasAddresss is true i.e apAddress data is populated on ops form, then assign apAddress
                    //(2) if hasAddress is false means data not populated on opsForm, check to see if apAddress originally is undefined 
                    // i.e not set in S3 file at all, then set to undefined (3)else set it to null to physically delete from RDS
                    apAddress: di.apEligibilityInd ? ( hasAddress  ? di.apAddress : (di?.apAddress === undefined? undefined:null) ) : undefined
                }
                },
             false,"di");
            request = {
                request: data,
                submitted: {}, //No user submission for this update
                original: record,
                meta: {
                    preparedBy: preparedByUser,
                    approvedBy: approvedByUser
                }
            };
            validated = true;
        } catch (error: any) {
            console.log('diProfileEditForm:validate error: '+ error)
            if (error instanceof ValidationError) {
                errors = ErrorManager(error.fieldErrors);
            }
        }
        //Submit Function
        const submit = async () => {
            setSubmitting(true);
            try {
                console.log('DiProfileEditForm:submit call: request is :' + JSON.stringify(request));
                if (validated) {
                    const response: IOrgModel = await API.post('AiFormRequest', `/save?key=saveDiProfile`, {
                        body: {
                            recordCategoryType: 'di',
                            ...request
                        }
                    }).then((response: any) => processResponse(response));

                    onComplete();
                } else {
                    throw new Error("The Di saveProfile failed as some of the entered fields are invalid.");
                }
            } catch (error: any) {
                setErrorMessage(error?.message || error);
                setErrorMessageOpen(true);
                console.log('diProfileEditForm:saveDiProfile API error :',error);
            }
            // Perform Redirect
            setSubmitting(false);
        };
        //End of Submit

        return <>

            <Dialog fullWidth={true} maxWidth={'md'} open={showLoading} onClose={disableDismiss}>
                <div style={{padding: '20px'}}>
                    <Loader/>
                </div>
            </Dialog>

            {errorMessage &&
                <div style={{marginBottom: '20px', marginTop: '20px'}}>
                    <ErrorNotice errorMessage={errorMessage}/>
                </div>
            }
            <Dialog fullWidth={true} maxWidth={'md'} open={errorMessageOpen}
                    onClose={enableDismiss((event, reason) => {
                        setErrorMessageOpen(false);
                    })}>
                <ContainerInline style={{padding: '20px'}}>
                    <HeaderInline style={{marginBottom: '6px'}}>
                        <i style={{marginRight: '10px'}}
                            className="cb-icon cb-icon-circular cb-exclamation cb-red1-color" aria-hidden="true"></i>
                        <h4 className="cb-notification-title"><strong>Errors Occurred</strong></h4>
                    </HeaderInline>
                    <div style={{marginLeft: '42px'}} className="cb-notification-content">
                        {errorMessage}
                    </div>
                </ContainerInline>
            </Dialog>
            <OrgFields
                item={org}
                type="di"
                original={record.org || undefined}
                defaults={{}}
                hints={{}}
                errors={errors.getFieldError('org')}
                showProtected={allowProtected}
                onUpdate={(data) => {
                    setOrg(data);
                }}></OrgFields>

            <DiFields
                item={di}
                original={record.di || undefined}
                errors={errors.getFieldError('di')}
                showProtected={allowProtected}
                onUpdate={(data) => {
                    setDi(data);
                }}></DiFields>

            <br/>

            <PrimaryButton style={{width: '100%', marginBottom: '10px'}} onClick={() => {
                submit();
            }} disabled={submitting}>Submit</PrimaryButton>
            <Button style={{marginBottom: '10px', width: '100%'}} onClick={() => {
                onClose();
            }}>Close</Button>
        </>;
    }
};